import { Component, Element, Event, EventEmitter, h, Host } from "@stencil/core";
import api from "../../utils/api";
import { getAttributesCamelCase } from "../../utils/getAttributesCamelCase";
import { filterObject } from "../../utils/filterObject";

/**
 * These common HTML attributes will not be considered for fetching data.
 * They are ignored to avoid unnecessary data fetching for standard HTML attributes.
 */
const ignoreAttributeList = ["class", "id", "style", "slot", "tabindex", "title"];

interface Child extends HTMLElement {
    [key: string]: unknown;
}

@Component({
    tag: "ux-data-ingest",
    styleUrl: "data-ingest.scss",
    shadow: false
})
export class DataIngest {
    /**
     * Event that is fired when a request is sent with the name of the property.
     * This can be used to indicate the start of a data fetch operation.
     */
    @Event({ eventName: "uxStartRequest" }) startRequest!: EventEmitter<string>;

    @Element() host!: HTMLUxDataIngestElement;

    /** Contains the filtered attributes with their URLs. */
    private attributes: { [key: string]: unknown } = {};

    /** Observes changes to the attributes. */
    private observer!: MutationObserver;

    componentWillLoad() {
        this.refreshAttributes();
        this.observer = new MutationObserver(() => this.refreshAttributes());
        this.observer.observe(this.host, {
            attributes: true,
            childList: false,
            subtree: false
        });
    }

    disconnectedCallback() {
        this.observer.disconnect();
    }

    /**
     * Reads the attributes and their URLs and matches them with the previous ones.
     * If there are changes, it triggers data loading for the updated attributes.
     */
    private refreshAttributes() {
        const oldAttributes = this.attributes;
        this.attributes = filterObject(getAttributesCamelCase(this.host), ignoreAttributeList);
        Object.keys(this.attributes).forEach((key) => {
            if (this.attributes[key] !== oldAttributes[key]) {
                this.loadData(key);
            }
        });
    }

    /**
     * Loads the data from the URL supplied via attribute and assigns it to the child element.
     * Handles both URL-based data fetching and inline script tag data.
     */
    private loadData(attributeName: string) {
        const url = this.attributes[attributeName] as string;
        if (url === "") {
            return;
        }
        if (url.charAt(0) === "#") {
            return this.getScriptTag(attributeName);
        }
        this.startRequest.emit(attributeName);
        const child = this.host.firstElementChild as Child;
        const loading = `${attributeName}Loading`;
        if (child === null) {
            return;
        }
        child[loading] = true;
        api(url)
            .then((result) => {
                child[loading] = false;
                child[attributeName] = result;
            })
            .catch(() => {
                child[loading] = false;
            });
    }

    /**
     * Retrieves data from a script tag within the component.
     * Supports both JSON and plain text script types.
     */
    private getScriptTag(attributeName: string) {
        const url = (this.attributes[attributeName] as string) || "";
        // We decided to use data-id instead of id because it cannot always be ensured that the id is unique.
        const tag = this.host.querySelector<HTMLScriptElement>(`[data-id="${url.replace("#", "")}"]`);
        if (!tag || tag == null || url.length < 2) {
            return;
        }
        const content = tag.innerHTML;
        let result;
        if (tag.type === "application/json") {
            result = JSON.parse(content);
        }
        if (tag.type === "text/plain") {
            result = String(content).trim();
        }
        const child = this.host.firstElementChild as Child;
        if (child === null) {
            return;
        }
        child[attributeName] = result;
    }

    render() {
        return (
            <Host class="ux-data-ingest">
                <slot />
            </Host>
        );
    }
}
